// Copyright (c) 2022 by Move Closer

import { ElementIdentifier } from '@movecloser/page-builder'

import {
  ImageData,
  IRelatedService,
  ModuleContentResolver,
  NavigationItem,
  AnyModule
} from '../../contexts'
import { logger } from '../../support'
import { snippetModulesResolver } from '../../support/snippet'

import { ResolvedLink } from '../partials'
import { resolveImage, resolveLink, resolveNavigation } from '../resolvers'

import { NavbarModuleContent, NavbarModuleContentInput } from './Navbar.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl> (edited)
 * @author Filip Rurak <filip.rurak@movecloser.pl> (edited)
 */
export const resolveNavbarModuleContent: ModuleContentResolver<NavbarModuleContentInput, NavbarModuleContent> =
  async (input: NavbarModuleContentInput, relatedService: IRelatedService): Promise<NavbarModuleContent> => {
    let mainNav: NavigationItem[] = []
    let externalNav: NavigationItem[] = []
    let languagesNav: NavigationItem[] = []
    let profileNav: NavigationItem[] = []
    let brandImage: ImageData | undefined
    let secondaryBrandImage: ImageData | undefined
    let link: ResolvedLink | undefined
    let logoLink: ResolvedLink | undefined
    let secondaryLogoLink: ResolvedLink | undefined

    if (typeof input.mainNav !== 'undefined' && input.mainNav) {
      try {
        mainNav = await resolveNavigation(input.mainNav, relatedService)

        if (mainNav.length > 0) {
          for (const item of mainNav) {
            if (typeof item.snippet !== 'undefined' && item.snippet.hasOwnProperty('modules') && typeof item.snippet.modules !== 'undefined') {
              let resolvedModules: Record<ElementIdentifier, AnyModule> = {}
              const modules = Object.values(item.snippet.modules)

              try {
                resolvedModules = await snippetModulesResolver(modules, relatedService)
              } catch (e) {
                logger(e, 'warn')
              }

              item.snippet.modules = resolvedModules
            }
          }
        }
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.externalNav !== 'undefined' && input.externalNav) {
      try {
        externalNav = await resolveNavigation(input.externalNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.languagesNav !== 'undefined' && input.languagesNav) {
      try {
        languagesNav = await resolveNavigation(input.languagesNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.profileNav !== 'undefined' && input.profileNav) {
      try {
        profileNav = await resolveNavigation(input.profileNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.brandImage !== 'undefined' && input.brandImage) {
      try {
        brandImage = await resolveImage(input.brandImage, relatedService)
        brandImage = {
          ...brandImage,
          width: input.brandImageDimensions?.maxWidth,
          height: input.brandImageDimensions?.maxHeight
        }
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.secondaryBrandImage !== 'undefined' && input.secondaryBrandImage) {
      try {
        secondaryBrandImage = await resolveImage(input.secondaryBrandImage, relatedService)
        secondaryBrandImage = {
          ...secondaryBrandImage,
          width: input.secondaryBrandImageDimensions?.maxWidth,
          height: input.secondaryBrandImageDimensions?.maxHeight
        }
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.link !== 'undefined' && input.link) {
      try {
        link = await resolveLink(input.link, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (input.logoLink && !input.logoLink.relatedTarget) {
      logoLink = input.logoLink as ResolvedLink
    }

    if (typeof input.logoLink !== 'undefined' && input.logoLink) {
      try {
        logoLink = await resolveLink(input.logoLink, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (input.secondaryLogoLink && !input.secondaryLogoLink.relatedTarget) {
      secondaryLogoLink = input.secondaryLogoLink as ResolvedLink
    }

    if (typeof input.secondaryLogoLink !== 'undefined' && input.secondaryLogoLink) {
      try {
        secondaryLogoLink = await resolveLink(input.secondaryLogoLink, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    return {
      ...input,
      brandImage,
      mainNav,
      externalNav,
      languagesNav,
      link,
      logoLink,
      profileNav,
      secondaryBrandImage,
      secondaryLogoLink
    }
  }
